class GraceCollection {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.customPriceEnabled = root.dataset.glCustomPriceEnabled === 'true';
    this.currency = root.dataset.glPriceCurrency || 'USD';
    this.priceFractionDigits = this.getCurrencyFractionDigits();
    this.bind();
    this.description();
    this.hydrateCustomPriceFromURL();
  }

  getCurrencyFractionDigits() {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).resolvedOptions().maximumFractionDigits;
    } catch {
      return 2;
    }
  }

  bind() {
    const toggle = this.root.querySelector('[data-gl-filter-toggle]');
    toggle?.addEventListener('click', () => {
      const hidden = this.root.classList.toggle('is-filters-hidden');
      toggle.setAttribute('aria-expanded', String(!hidden));
      toggle.querySelector('[data-gl-filter-label]').textContent = hidden ? 'Show filters' : 'Hide filters';
    });

    this.root.querySelector('[data-gl-sort]')?.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      window.location.assign(url);
    });

    const drawer = this.root.querySelector('[data-gl-filter-drawer]');
    this.root.querySelector('[data-gl-open-drawer]')?.addEventListener('click', () => drawer?.showModal());
    this.root.querySelectorAll('[data-gl-close-drawer]').forEach((button) => button.addEventListener('click', () => drawer?.close()));
    this.root.querySelector('[data-gl-load-more]')?.addEventListener('click', (event) => this.load(event));

    if (!this.customPriceEnabled) return;
    this.root.querySelectorAll('[data-gl-custom-price-apply]').forEach((button) => button.addEventListener('click', () => this.applyCustomPrice()));
    this.root.querySelectorAll('[data-gl-custom-price-clear]').forEach((button) => button.addEventListener('click', () => this.clearCustomPrice()));
    this.root.querySelectorAll('[data-gl-custom-price-min], [data-gl-custom-price-max]').forEach((input) => input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyCustomPrice();
      }
    }));
  }

  description() {
    const copy = this.root.querySelector('[data-gl-description-copy]');
    const button = this.root.querySelector('[data-gl-description-toggle]');
    if (!copy || !button || copy.scrollHeight < 130) return;
    copy.classList.add('is-collapsed');
    button.hidden = false;
    button.addEventListener('click', () => {
      const collapsed = copy.classList.toggle('is-collapsed');
      button.textContent = collapsed ? 'Read more' : 'Read less';
    });
  }

  parsePrice(value) {
    if (!value || !value.trim()) return null;
    let normalized = value.replace(/[^\d,.-]/g, '');
    if (!/\d/.test(normalized)) return null;
    const comma = normalized.lastIndexOf(',');
    const point = normalized.lastIndexOf('.');
    if (comma >= 0 && point >= 0) normalized = comma > point ? normalized.replace(/\./g, '').replace(',', '.') : normalized.replace(/,/g, '');
    else if (comma >= 0) {
      const digitsAfterComma = normalized.length - comma - 1;
      normalized = this.priceFractionDigits > 0 && digitsAfterComma === this.priceFractionDigits ? normalized.replace(',', '.') : normalized.replace(/,/g, '');
    }
    const amount = Number(normalized);
    return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * (10 ** this.priceFractionDigits)) : null;
  }

  formatPrice(minorUnits) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: this.priceFractionDigits }).format(minorUnits / (10 ** this.priceFractionDigits));
  }

  queryPrice(minorUnits) {
    return (minorUnits / (10 ** this.priceFractionDigits)).toFixed(this.priceFractionDigits).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  }

  normalizedState(state) {
    return state.min !== null && state.max !== null && state.min > state.max ? { min: state.max, max: state.min } : state;
  }

  urlPriceState() {
    const params = new URL(window.location.href).searchParams;
    return this.normalizedState({ min: this.parsePrice(params.get('price_min') || ''), max: this.parsePrice(params.get('price_max') || '') });
  }

  inputPriceState() {
    return this.normalizedState({
      min: this.parsePrice(this.root.querySelector('[data-gl-custom-price-min]')?.value || ''),
      max: this.parsePrice(this.root.querySelector('[data-gl-custom-price-max]')?.value || ''),
    });
  }

  setPriceInputs({ min, max }) {
    this.root.querySelectorAll('[data-gl-custom-price-min]').forEach((input) => { input.value = min === null ? '' : this.formatPrice(min); });
    this.root.querySelectorAll('[data-gl-custom-price-max]').forEach((input) => { input.value = max === null ? '' : this.formatPrice(max); });
  }

  hydrateCustomPriceFromURL() {
    if (!this.customPriceEnabled) return;
    const state = this.urlPriceState();
    this.setPriceInputs(state);
    this.filterByCustomPrice(state);
  }

  applyCustomPrice() {
    const state = this.inputPriceState();
    const url = new URL(window.location.href);
    state.min === null ? url.searchParams.delete('price_min') : url.searchParams.set('price_min', this.queryPrice(state.min));
    state.max === null ? url.searchParams.delete('price_max') : url.searchParams.set('price_max', this.queryPrice(state.max));
    url.searchParams.delete('page');
    history.pushState({ glCustomPrice: true }, '', url);
    this.setPriceInputs(state);
    this.filterByCustomPrice(state);
  }

  clearCustomPrice() {
    const url = new URL(window.location.href);
    url.searchParams.delete('price_min');
    url.searchParams.delete('price_max');
    url.searchParams.delete('page');
    history.pushState({ glCustomPrice: true }, '', url);
    const state = { min: null, max: null };
    this.setPriceInputs(state);
    this.filterByCustomPrice(state);
  }

  filterByCustomPrice({ min, max }) {
    const products = [...this.root.querySelectorAll('[data-gl-price-min]')];
    let visible = 0;
    products.forEach((product) => {
      const price = Number(product.dataset.glPriceMin);
      const matches = Number.isFinite(price) && (min === null || price >= min) && (max === null || price <= max);
      product.hidden = !matches;
      product.setAttribute('aria-hidden', String(!matches));
      if (matches) visible += 1;
    });
    const active = min !== null || max !== null;
    this.root.querySelectorAll('[data-gl-custom-price-clear]').forEach((button) => { button.hidden = !active; });
    const empty = this.root.querySelector('[data-gl-price-empty]');
    if (empty) empty.hidden = visible !== 0;
    this.root.querySelectorAll('[data-gl-editorial]').forEach((editorial) => { editorial.hidden = visible === 0; });
    const count = this.root.querySelector('[data-gl-results-number]');
    const label = this.root.querySelector('[data-gl-results-label]');
    if (count) count.textContent = String(visible);
    if (label) label.textContent = visible === 1 ? 'product' : 'products';
  }

  async load(event) {
    event.preventDefault();
    const link = event.currentTarget;
    if (link.dataset.loading) return;
    link.dataset.loading = 'true';
    link.setAttribute('aria-busy', 'true');
    link.textContent = 'Loading…';
    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url);
      if (!response.ok) throw new Error();
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = parsed.querySelector('[data-gl-grid]');
      const grid = this.root.querySelector('[data-gl-grid]');
      if (!nextGrid || !grid) throw new Error();
      [...nextGrid.children].filter((item) => !item.matches('[data-gl-editorial]')).forEach((item) => grid.append(item));
      const nextLink = parsed.querySelector('[data-gl-load-more]');
      const loaded = grid.querySelectorAll('.gl-collection__product').length;
      const total = Number(grid.dataset.totalCount || loaded);
      this.root.querySelector('[data-gl-loaded]').textContent = loaded;
      this.root.querySelector('[data-gl-progress]').style.width = `${Math.min(100, (loaded / total) * 100)}%`;
      if (nextLink) {
        link.href = nextLink.href;
        link.dataset.loading = '';
        link.removeAttribute('aria-busy');
        link.textContent = 'Load more';
      } else link.remove();
    } catch {
      window.location.assign(link.href);
    }
  }
}

function mountCollections(scope = document) {
  const roots = scope.matches?.('[data-gl-collection]') ? [scope] : scope.querySelectorAll?.('[data-gl-collection]') || [];
  roots.forEach((root) => {
    if (root.dataset.glCollectionMounted === 'true') return;
    root.dataset.glCollectionMounted = 'true';
    root._glCollection = new GraceCollection(root);
  });
}

mountCollections();
new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
  if (node.nodeType === Node.ELEMENT_NODE) mountCollections(node);
}))).observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener('popstate', () => document.querySelectorAll('[data-gl-collection]').forEach((root) => root._glCollection?.hydrateCustomPriceFromURL()));
